import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import CustomLink from '../CustomLink';
import { Logo } from '../Logo';
import { useUser } from '../../shared/use-user';
import { useRouting } from '../../shared/use-routing';
import { pagePathnames } from '../../pages/page-routes';
import { logEvent } from '../../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../../lib/shared/event-types';
import { SUPPORT_EMAIL } from '../../shared/links';
import { getLegalEntity, getLegalLinks } from '../../app-ui-config';
import styles from './FooterNew.css';

const cx = classnames.bind(styles);

function FooterNew({ className }) {
    const { userData } = useUser();
    const { params, buildPath, lang } = useRouting();
    const { t } = useTranslation();
    const classes = cx({
        root: true,
        [className]: Boolean(className)
    });

    const legalLinks = getLegalLinks(userData.legalRegion, lang);
    const legalEntity = getLegalEntity(userData.legalRegion);

    if (Boolean(params.subscriptionId) || Boolean(params.orderId)) {
        return <div />;
    }

    return (
        <footer className={classes}>
            <div className={styles.wrapper}>
                <div className={styles.topRow}>
                    <div className={styles.column}>
                        <Logo url={buildPath(pagePathnames.start)} />
                        <div className={styles.info}>
                            <div className={styles.infoItem}>
                                <p className={styles.infoTitle}>{t('common.address')}</p>
                                <p className={styles.infoText}>{legalEntity.address}</p>
                            </div>
                            <div className={styles.infoItem}>
                                <p className={styles.infoTitle}>{t('common.contact')}</p>
                                <div className={styles.infoText}>
                                    {Boolean(legalEntity.telephone) && (
                                        <CustomLink
                                            to={legalEntity.telephone.link}
                                            target='_blank'
                                            className={styles.infoLink}
                                        >
                                            {legalEntity.telephone.text}
                                        </CustomLink>
                                    )}
                                    <CustomLink
                                        to={`mailto:${SUPPORT_EMAIL}`}
                                        target='_blank'
                                        className={styles.infoLink}
                                        onClick={() => logEvent(EVENT_TYPES.SUPPORT_EMAIL_CLICKED)}
                                    >
                                        {SUPPORT_EMAIL}
                                    </CustomLink>
                                </div>
                            </div>
                        </div>
                    </div>
                    <nav aria-label={t('common.mainNavigation')}>
                        <ul className={styles.navLinks}>
                            <li>
                                <CustomLink
                                    to={buildPath(pagePathnames.aboutUs)}
                                    className={styles.navLink}
                                >
                                    {t('common.aboutUs')}
                                </CustomLink>
                            </li>
                            <li>
                                <CustomLink
                                    to={buildPath(pagePathnames.contactUs)}
                                    className={styles.navLink}
                                >
                                    {t('common.contactUsLink')}
                                </CustomLink>
                            </li>
                        </ul>
                    </nav>
                </div>
                <div className={styles.divider} />
                <div className={styles.bottomRow}>
                    <p className={styles.copyright}>
                        © {legalEntity.year} — {new Date().getFullYear()} {legalEntity.company}
                    </p>
                    <nav aria-label={t('common.legalLinks')}>
                        <ul className={styles.legalLinks}>
                            <li>
                                <CustomLink
                                    to={legalLinks.privacyPolicy}
                                    target='_blank'
                                    className={styles.legalLink}
                                >
                                    {t('common.privacyPolicy')}
                                </CustomLink>
                            </li>
                            <li>
                                <CustomLink
                                    to={legalLinks.termsOfUse}
                                    target='_blank'
                                    className={styles.legalLink}
                                >
                                    {t('common.termsOfUse')}
                                </CustomLink>
                            </li>
                            <li>
                                <CustomLink
                                    to={legalLinks.cookiePolicy}
                                    target='_blank'
                                    className={styles.legalLink}
                                >
                                    {t('common.cookiesSettings')}
                                </CustomLink>
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>
        </footer>
    );
}

FooterNew.propTypes = {
    className: PropTypes.string
};

FooterNew.defaultProps = {
    className: ''
};

export {
    FooterNew
};
